/**
 * LOS card down payments (spec LOS_CardDownPayments A1, A8, A12, A13): the API's shapes and the rules the screens
 * share. Kept free of the app's runtime (store, auth, shared library) so Jest can load it on its own.
 */

/** `GET api/DownPayment/CardConfig`'s billing prefill: the primary applicant. */
export interface DownPaymentCardBillTo {
  nameOnAccount?: string | null;
  street?: string | null;
  city?: string | null;
  state?: string | null;
  zip?: string | null;
}

/** One card down payment as los-api shows it (`DownPaymentCardChargeDTO`). */
export interface DownPaymentCardCharge {
  ledgerId: string;
  clientRequestId?: string | null;
  /** The ledger status name, e.g. `Posted`, `Declined`, `PostRefused`. */
  status: string;
  /** The status in staff's words. */
  statusText: string;
  amount: number;
  accountName?: string | null;
  brand?: string | null;
  last4?: string | null;
  vendorTransactionId?: string | null;
  receiptNumber?: string | null;
  /** The TransactionID AMS keeps as the deposit's check number. */
  amsTransactionId?: string | null;
  /** What happened and what to do. */
  message?: string | null;
  /** AMS doesn't have it yet (A12): it blocks other down payments and posting. */
  unfinished: boolean;
  createdDate: string;
  actions: CardAction[];
}

/** `GET api/DownPayment/CardConfig` (A2). */
export interface DownPaymentCardConfig {
  /** AMS pay source codes that take card payments; empty while the client's card payments are off. */
  cardPaySources: string[];
  available: boolean;
  reason?: string | null;
  accountName?: string | null;
  /** `Sandbox` or `Production`. */
  environment?: string | null;
  apiLoginId?: string | null;
  clientKey?: string | null;
  scriptUrl?: string | null;
  billTo?: DownPaymentCardBillTo | null;
  unfinishedCharge?: DownPaymentCardCharge | null;
}

/** A manual action's answer (A10, A11). */
export interface DownPaymentCardActionResult {
  charge: DownPaymentCardCharge;
  message?: string | null;
}

/** `POST api/DownPayment/ChargeCard`'s body (A6). */
export interface DownPaymentCardChargeRequest {
  clientRequestId: string;
  applicationId: string;
  amountTendered: number;
  paymentSourceId: string;
  deliveryMethodId: string;
  locationOfPaymentId: string;
  locationOfSaleId: string;
  cashDrawerId: string;
  vehicleId?: string | null;
  spotVehicleAfterConfirmation: boolean;
  method: {
    dataDescriptor: string;
    dataValue: string;
    nameOnAccount: string;
    billingStreet: string;
    billingCity: string;
    billingState: string;
    billingZip: string;
  };
}

export type CardAction = 'Check' | 'RetryPosting' | 'RecordReceipt' | 'Resolve' | 'RetryReversal';

/** A13's button labels, by action. */
export const CARD_ACTION_LABELS: Record<CardAction, string> = {
  Check: 'Check status',
  RetryPosting: 'Retry posting',
  RecordReceipt: 'Record AMS receipt',
  Resolve: 'Mark resolved',
  RetryReversal: 'Retry reversal',
};

/** Statuses whose card reversal is waiting or failed: the window lists them with the unfinished ones (A13). */
const REVERSAL_STATUSES = ['VendorReversalPending', 'VendorReversalFailed'];

/** Statuses that charged nothing: the dialog stays on the form with a new request id (A8). */
const NOTHING_CHARGED_STATUSES = ['Declined', 'NotCharged'];

/** A8: the charge request got no answer. */
export const CHARGE_ANSWER_LOST =
  "The answer was lost. Don't charge the card again. Use Check status under Card payments.";

/** A4: Accept.js didn't load. */
export const ACCEPT_COULD_NOT_LOAD = "Authorize.Net's card form couldn't load. Reload the page.";

const normalizedCode = (code: string | null | undefined) => (code ?? '').trim().toUpperCase();

/** As the server and the rest of the client format money: "$1,234.56". */
export const money = (amount: number | null | undefined) =>
  new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 2 }).format(amount ?? 0);

/**
 * A1: the dialog is in card mode when the chosen pay source's AMS code is one of the client's card pay sources and the
 * LOS doesn't send that pay source to another processor (REPAY, BlytzPay, PayNearMe). The server answers no card pay
 * sources while the client's card payments are off, so that switch needs no check here.
 */
export const isCardPaySource = (
  cardPaySources: readonly string[] | null | undefined,
  paymentSource: { name?: string | null; paymentProcessorId?: string | null } | null | undefined,
  amsPaymentProcessorId: string,
): boolean => {
  const code = normalizedCode(paymentSource?.name);
  if (!code || !cardPaySources?.length) return false;
  const processor = paymentSource?.paymentProcessorId?.trim().toLowerCase();
  if (processor && processor !== amsPaymentProcessorId.toLowerCase()) return false;
  return cardPaySources.some((source) => normalizedCode(source) === code);
};

/**
 * A4, A12: why Charge card is off before anything is sent. A string is the reason to show; null means off without a
 * reason yet (the settings or Accept.js are still loading); undefined means the card can be charged.
 */
export const chargeCardRefusal = (
  config: DownPaymentCardConfig | null | undefined,
  acceptStatus: 'idle' | 'loading' | 'ready' | 'failed' | 'blocked',
): string | null | undefined => {
  if (!config) return null;
  if (config.unfinishedCharge) return unfinishedChargeDialogText(config.unfinishedCharge);
  if (!config.available) return config.reason?.trim() || null;
  if (acceptStatus === 'failed' || acceptStatus === 'blocked') return ACCEPT_COULD_NOT_LOAD;
  if (acceptStatus !== 'ready') return null;
  return undefined;
};

/** A8: a Declined or NotCharged answer keeps the user on the form; anything else shows the result panel. */
export const chargeTookNothing = (status: string | null | undefined) => NOTHING_CHARGED_STATUSES.includes(status ?? '');

export const unfinishedCharges = (charges: readonly DownPaymentCardCharge[] | null | undefined) =>
  (charges ?? []).filter((charge) => charge.unfinished);

/** A13: the rows the Card payments window lists: unfinished, or a reversal pending or failed. */
export const chargesToActOn = (charges: readonly DownPaymentCardCharge[] | null | undefined) =>
  (charges ?? []).filter((charge) => charge.unfinished || REVERSAL_STATUSES.includes(charge.status));

const unfinishedTotal = (charges: readonly DownPaymentCardCharge[] | null | undefined) =>
  unfinishedCharges(charges).reduce((sum, charge) => sum + Math.round(charge.amount * 100), 0) / 100;

/** A12's bar on the application, or undefined when every card down payment is in AMS. */
export const unfinishedCardBarText = (charges: readonly DownPaymentCardCharge[] | null | undefined): string | undefined =>
  unfinishedCharges(charges).length === 0
    ? undefined
    : `A card down payment of ${money(unfinishedTotal(charges))} isn't recorded in AMS yet.`;

/** A12's refusal of posting, or undefined. */
export const unfinishedCardPostingRefusal = (charges: readonly DownPaymentCardCharge[] | null | undefined): string | undefined =>
  unfinishedCharges(charges).length === 0
    ? undefined
    : `A card down payment of ${money(unfinishedTotal(charges))} isn't recorded in AMS yet. Finish it under Card payments first.`;

/** A12's banner in the Down Payment dialog for the server's unfinished charge. */
export const unfinishedChargeDialogText = (charge: DownPaymentCardCharge) =>
  `A card payment of ${money(charge.amount)} for this application isn't finished (${charge.statusText}). Finish it under Card payments first.`;

/** "Visa ending 1111", or undefined when the card's last four digits aren't known. */
export const cardEnding = (charge: Pick<DownPaymentCardCharge, 'brand' | 'last4'>): string | undefined => {
  const last4 = charge.last4?.trim();
  if (!last4) return undefined;
  const brand = charge.brand?.trim();
  return `${brand || 'Card'} ending ${last4}`;
};

type DepositKeys = { receipt?: string | null; transactionId?: string | null };

/** A deposit is a card row's by the TransactionID AMS keeps (when the deposit history carries it) or by its AMS receipt. */
const depositIsCharges = (deposit: DepositKeys, charge: Pick<DownPaymentCardCharge, 'receiptNumber' | 'amsTransactionId'>) => {
  const transactionId = normalizedCode(deposit.transactionId);
  const receipt = (deposit.receipt ?? '').trim();
  return (
    (!!transactionId && normalizedCode(charge.amsTransactionId) === transactionId) ||
    (!!receipt && (charge.receiptNumber ?? '').trim() === receipt)
  );
};

/** A13: the card behind a deposit, as "Visa ending 1111" (the history's column is headed "Card"). */
export const depositCardLabel = (
  deposit: DepositKeys,
  charges: readonly DownPaymentCardCharge[] | null | undefined,
): string | undefined => {
  const charge = (charges ?? []).find((row) => depositIsCharges(deposit, row));
  return charge ? cardEnding(charge) : undefined;
};

/** A8: the deposit a posted card down payment made, whose receipt the result prints; none until the history has it. */
export const depositForCharge = <T extends DepositKeys>(
  charge: Pick<DownPaymentCardCharge, 'receiptNumber' | 'amsTransactionId'>,
  deposits: readonly T[] | null | undefined,
): T | undefined => (deposits ?? []).find((deposit) => depositIsCharges(deposit, charge));

/** The deposit's account name with "Sandbox" for a sandbox account (A1). */
export const accountLabel = (config: Pick<DownPaymentCardConfig, 'accountName' | 'environment'>): string | undefined => {
  const name = config.accountName?.trim();
  if (!name) return undefined;
  return config.environment?.trim().toLowerCase() === 'sandbox' ? `${name} (Sandbox)` : name;
};
